import React from 'react';
import { Box, Button, DialogActions, alpha, useTheme } from '@mui/material';
import { OperationProgressIndicator } from '../progress';

interface FindReplaceDialogActionsProps {
  readonly previewOnly: boolean;
  readonly isReplacing: boolean;
  readonly canRunSearch: boolean;
  readonly canReplaceCurrent: boolean;
  readonly canReplaceAll: boolean;
  readonly isReplacingCurrent: boolean;
  readonly isReplacingAll: boolean;
  readonly onClose: () => void;
  readonly onFind: () => void;
  readonly onReplaceCurrent: () => void | Promise<void>;
  readonly onReplaceAll: () => void | Promise<void>;
}

export const FindReplaceDialogActions: React.FC<FindReplaceDialogActionsProps> = ({
  previewOnly,
  isReplacing,
  canRunSearch,
  canReplaceCurrent,
  canReplaceAll,
  isReplacingCurrent,
  isReplacingAll,
  onClose,
  onFind,
  onReplaceCurrent,
  onReplaceAll,
}) => {
  const theme = useTheme();
  const surfaceBorder = alpha(theme.palette.common.white, 0.06);

  return (
    <DialogActions
      disableSpacing
      sx={{
        px: 2,
        py: 1.25,
        gap: 1,
        borderTop: `1px solid ${surfaceBorder}`,
        backgroundColor: alpha(theme.palette.common.white, 0.02),
      }}
    >
      <Button
        onClick={onClose}
        size="small"
        disabled={previewOnly}
        sx={{
          color: theme.palette.text.secondary,
          minWidth: 60,
          '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.05) },
        }}
      >
        Close
      </Button>

      <Box sx={{ flex: 1 }} />

      <Button
        onClick={onFind}
        variant="outlined"
        size="small"
        disabled={previewOnly || isReplacing || !canRunSearch}
        sx={{
          minWidth: 70,
          borderColor: alpha(theme.palette.common.white, 0.1),
          '&:hover': { borderColor: alpha(theme.palette.common.white, 0.2) },
        }}
      >
        Find all
      </Button>
      <Button
        onClick={onReplaceCurrent}
        disabled={previewOnly || isReplacing || !canReplaceCurrent}
        variant="outlined"
        size="small"
        sx={{
          minWidth: 80,
          borderColor: alpha(theme.palette.common.white, 0.1),
          '&:hover': { borderColor: alpha(theme.palette.common.white, 0.2) },
        }}
      >
        {isReplacingCurrent
          ? <OperationProgressIndicator size={14} color="inherit" ariaLabel="Replacing current match" />
          : 'Replace'}
      </Button>
      <Button
        onClick={onReplaceAll}
        disabled={previewOnly || isReplacing || !canReplaceAll}
        variant="contained"
        size="small"
        sx={{
          minWidth: 80,
          backgroundColor: alpha(theme.palette.primary.main, 0.9),
          '&:hover': { backgroundColor: theme.palette.primary.main },
        }}
      >
        {isReplacingAll
          ? <OperationProgressIndicator size={14} color="inherit" ariaLabel="Replacing all matches" />
          : 'Replace all'}
      </Button>
    </DialogActions>
  );
};
